'use client';

import { memo } from 'react';
import { HelpCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import type { ChatMessage } from '@/lib/interview/types';
import { hideQuickReplyMarker } from '@/lib/interview/quick-replies';

interface MessageBubbleProps {
  message: ChatMessage;
  isHelpMessage?: boolean;
}

export const MessageBubble = memo(function MessageBubble({ message, isHelpMessage }: MessageBubbleProps) {
  const isAssistant = message.role === 'assistant';
  const isHelp = isHelpMessage || message.isHelpMessage;

  // Determine bubble styling based on role and help status
  const getBubbleStyles = () => {
    if (isHelp) {
      // Help messages have distinct teal/cyan styling
      return isAssistant
        ? 'bg-teal/10 border border-teal/20 text-off-black rounded-bl-md'
        : 'bg-teal/20 border border-teal/30 text-off-black rounded-br-md';
    }
    // Regular messages
    return isAssistant
      ? 'bg-white border border-off-black/10 text-off-black rounded-bl-md'
      : 'bg-off-black text-off-white rounded-br-md';
  };

  return (
    <div className={`flex ${isAssistant ? 'justify-start' : 'justify-end'}`}>
      <div
        className={`
          max-w-[85%] md:max-w-[75%] px-4 py-3 rounded-2xl
          ${getBubbleStyles()}
          ${message.isStreaming ? 'animate-pulse-subtle' : ''}
        `}
      >
        {/* Help label for help messages */}
        {isHelp && (
          <div className="flex items-center gap-1.5 mb-2 pb-2 border-b border-teal/20">
            <HelpCircle className="w-3.5 h-3.5 text-teal" />
            <span className="text-[10px] font-medium text-teal uppercase tracking-wider">
              {isAssistant ? 'Snelle Hulp' : 'Hulpvraag'}
            </span>
          </div>
        )}

        {isAssistant ? (
          <div className="prose prose-sm max-w-none text-off-black">
            <ReactMarkdown
              components={{
                p: ({ children }) => <p className="mb-2 last:mb-0 text-off-black">{children}</p>,
                strong: ({ children }) => (
                  <strong className="font-semibold text-off-black">{children}</strong>
                ),
                ul: ({ children }) => <ul className="list-disc pl-4 mb-2 text-off-black">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 text-off-black">{children}</ol>,
                li: ({ children }) => <li className="mb-1 text-off-black">{children}</li>,
              }}
            >
              {hideQuickReplyMarker(message.content) || '...'}
            </ReactMarkdown>
          </div>
        ) : (
          <p className={`whitespace-pre-wrap ${isHelp ? 'text-off-black' : 'text-off-white'}`}>{message.content}</p>
        )}

        {/* Timestamp */}
        <div
          className={`
            text-[11px] mt-1
            ${isAssistant || isHelp ? 'text-off-black/40' : 'text-off-white/50'}
          `}
        >
          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  );
});
